import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { isWithinInterval, parseISO, format } from 'date-fns';
import Modal from 'react-modal';
import {
  ModalHeader,
  ModalTitle,
  CloseButton,
  CloseButtonText,
  ActivityItem,
  ActivityCheckbox,
  ActivityCheckboxIcon,
  ActivityInfo,
  ActivityLeftInfo,
  ActivityTitle,
  ActivityDistance,
  ActivityDate,
  DistanceInfo,
  DistanceText,
  ConfirmButton,
  ConfirmButtonText,
  EmptyText,
  ScrollContainer,
  modalStyle,
} from './styles';
import { Activity } from '../../store/Activities.store';
import { VirtualRaceType, UserVirtualRaceType } from '../../types/VirtualRace';

interface Props {
  visible: boolean;
  close: () => void;
  activities: Activity[];
  virtualRace: VirtualRaceType;
  userVirtualRace: UserVirtualRaceType;
  onConfirm: (selectedIds: number[]) => void;
}

export default function LinkActivitiesModal({
  visible,
  close,
  activities,
  virtualRace,
  userVirtualRace,
  onConfirm,
}: Props) {
  const [selectedActivities, setSelectedActivities] = useState<number[]>(
    userVirtualRace.activity_ids || [],
  );

  const validActivities = useMemo(() => {
    const startDate = new Date(virtualRace.startDate);
    const endDate = new Date(virtualRace.endDate);

    return activities.filter((activity) => {
      if (!['Run', 'Walk', 'Hike'].includes(activity.type)) return false;

      if (!activity.start_date) return false;
      const activityDate = parseISO(activity.start_date);
      if (!isWithinInterval(activityDate, { start: startDate, end: endDate })) {
        return false;
      }

      if (virtualRace.isOnlyGpsRegister) {
        return activity.summary_polyline;
      }

      return true;
    });
  }, [activities, virtualRace]);

  const selectedDistance = useMemo(() => validActivities
    .filter((activity) => selectedActivities.includes(activity.id))
    .reduce((sum, activity) => sum + (activity.distance || 0), 0), [selectedActivities, validActivities]);

  const toggleActivity = (activityId: number) => {
    if (selectedActivities.includes(activityId)) {
      setSelectedActivities(selectedActivities.filter((id) => id !== activityId));
    } else if (virtualRace.isSingleRegisterActivity) {
      setSelectedActivities([activityId]);
    } else {
      setSelectedActivities([...selectedActivities, activityId]);
    }
  };

  const handleConfirm = () => {
    if (selectedActivities.length === 0) {
      toast.error('Selecione pelo menos uma atividade');
      return;
    }

    if (selectedDistance < userVirtualRace.distance) {
      toast.error(
        `A distância selecionada (${selectedDistance.toFixed(2)} km) é menor que a distância necessária (${userVirtualRace.distance} km).`,
      );
      return;
    }

    onConfirm(selectedActivities);
    close();
  };

  if (!visible) return null;

  return (
    <Modal isOpen={visible} onRequestClose={close} style={modalStyle}>
      <ModalHeader>
        <ModalTitle>
          {virtualRace.isSingleRegisterActivity ? 'Vincular Atividade' : 'Vincular Atividades'}
        </ModalTitle>
        <CloseButton onClick={close}>
          <CloseButtonText>✕</CloseButtonText>
        </CloseButton>
      </ModalHeader>

      <DistanceInfo>
        <DistanceText>
          {`Distância selecionada: ${selectedDistance.toFixed(2)} / ${userVirtualRace.distance} km`}
        </DistanceText>
      </DistanceInfo>

      <ScrollContainer>
        {validActivities.length === 0 ? (
          <EmptyText>
            Nenhuma atividade encontrada no período da corrida virtual
          </EmptyText>
        ) : (
          validActivities.map((activity) => {
            const isSelected = selectedActivities.includes(activity.id);
            return (
              <ActivityItem
                key={activity.id}
                onClick={() => toggleActivity(activity.id)}
              >
                <ActivityCheckbox selected={isSelected}>
                  {isSelected && <ActivityCheckboxIcon>✓</ActivityCheckboxIcon>}
                </ActivityCheckbox>
                <ActivityInfo>
                  <ActivityLeftInfo>
                    <ActivityTitle>{activity.title}</ActivityTitle>
                    <ActivityDistance>
                      {activity.distance?.toFixed(2)}
                      {' '}
                      km
                    </ActivityDistance>
                  </ActivityLeftInfo>
                  {activity.start_date && (
                    <ActivityDate>
                      {format(parseISO(activity.start_date), 'dd/MM')}
                    </ActivityDate>
                  )}
                </ActivityInfo>
              </ActivityItem>
            );
          })
        )}
      </ScrollContainer>

      {validActivities.length > 0 && (
        <ConfirmButton onClick={handleConfirm}>
          <ConfirmButtonText>CONFIRMAR VINCULAÇÃO</ConfirmButtonText>
        </ConfirmButton>
      )}
    </Modal>
  );
}
